// F16 — the shared shell for the landing suite.
//
// Tailwind is kept for responsive visibility and grid only (inline styles
// cannot express a media query, and the breakpoints were already load-bearing
// here). Everything visual — colour, type, spacing, radii — comes from
// tokens.js. No hex literals, no physical-direction CSS: the site ships in
// Arabic, so anything that would not mirror is a bug.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { C, A, FONT, TYPE, SP, R, W, S } from './tokens';
import ArchMark from './ArchMark';
import StoreBadges from './StoreBadges';

// ---- style fragments -------------------------------------------------------
// Module scope, not inside the component: they depend only on tokens, and
// re-creating them per render would churn every style object needlessly.

const navLink = (active) => ({
    ...TYPE.body,
    color: active ? C.ink : C.inkMuted,
    fontWeight: active ? 500 : 400,
    textDecoration: 'none',
    paddingBlock: SP.xs,
    borderBlockEnd: `1px solid ${active ? C.ink : 'transparent'}`,
    transition: 'color .18s ease, border-color .18s ease',
    whiteSpace: 'nowrap',
    background: 'none',
    cursor: 'pointer',
});

// The dropdown is two layers on purpose.
//
// `dropdownBridge` is an invisible wrapper whose TOP PADDING spans the gap
// between the trigger and the visible card. The card is absolutely positioned,
// so it contributes nothing to the trigger's layout box; with a plain
// `marginBlockStart` that gap belonged to neither element, and moving the
// cursor down toward a menu item crossed dead space, fired mouseleave on the
// wrapper and shut the menu before it could be clicked. Padding on a
// hoverable ancestor keeps the pointer inside the subtree the whole way.
const dropdownBridge = {
    position: 'absolute',
    insetBlockStart: '100%',
    insetInlineStart: 0,
    paddingBlockStart: SP.sm,
    zIndex: 50,
};

const dropdownPanel = {
    minWidth: 190,
    background: C.surface,
    border: `1px solid ${C.line}`,
    borderRadius: R.md,
    boxShadow: `0 8px 28px ${A.inkShadow}`,
    paddingBlock: SP.sm,
};

const dropdownItem = (active) => ({
    display: 'block',
    ...TYPE.body,
    color: active ? C.ink : C.inkMuted,
    fontWeight: active ? 500 : 400,
    textDecoration: 'none',
    paddingBlock: SP.sm,
    paddingInline: SP.lg,
    transition: 'color .18s ease, background .18s ease',
});

const footerLink = {
    ...TYPE.body,
    color: A.paperFaint,
    textDecoration: 'none',
    transition: 'color .18s ease',
    background: 'none',
    cursor: 'pointer',
    textAlign: 'start',
    padding: 0,
};

const footerHeading = {
    ...TYPE.label,
    color: A.paperMuted,
    marginBlockEnd: SP.lg,
};

// DropdownMenu and LanguageToggle live at MODULE scope deliberately.
//
// Declared inside LandingLayout's body they would be a new function reference
// on every render, so React would treat them as a different component type and
// unmount + remount the whole subtree instead of updating it. LandingLayout
// re-renders on every nav hover (activeDropdown) and on every scroll boundary
// crossing, so the practical effect was: a keyboard user pressing Enter on the
// dropdown button lost focus immediately, because the focused node was removed
// from the document — Tab then restarted from the top of the page. The chevron
// transition never animated either, since the element was always freshly
// mounted in its final state. Everything they need comes in as props.

const DropdownMenu = ({ section, isOpen, isActivePage, onOpen, onLeave, onClose, onToggle }) => {
    const hasActiveItem = section.items?.some((item) => isActivePage(item.path));

    return (
        <div
            style={{ position: 'relative' }}
            onMouseEnter={onOpen}
            onMouseLeave={onLeave}
        >
            <button
                type="button"
                onClick={onToggle}
                aria-expanded={isOpen}
                aria-haspopup="true"
                style={{ ...navLink(hasActiveItem), display: 'inline-flex', alignItems: 'center', gap: SP.xs, border: 'none', borderBlockEnd: `1px solid ${hasActiveItem ? C.ink : 'transparent'}` }}
            >
                <span>{section.title}</span>
                <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    aria-hidden="true"
                    style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform .18s ease' }}
                >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {isOpen && (
                <div style={dropdownBridge}>
                    <div style={dropdownPanel}>
                        {section.items.map((item) => (
                            <Link
                                key={item.path}
                                to={item.path}
                                onClick={onClose}
                                style={dropdownItem(isActivePage(item.path))}
                                onMouseEnter={(e) => { e.currentTarget.style.background = C.paper; e.currentTarget.style.color = C.ink; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = isActivePage(item.path) ? C.ink : C.inkMuted; }}
                            >
                                {item.title}
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

// A globe, not an EN | العربية text pair. The icon reads as "language" at a
// glance in either script and does not grow the header when the locale
// changes. The current language rides beside it as a short code so the
// control still says what it will do.
const LanguageToggle = ({ language, toggleLanguage, t, block = false }) => (
    <button
        type="button"
        onClick={toggleLanguage}
        aria-label={language === 'en' ? t('Switch to Arabic') : t('Switch to English')}
        title={language === 'en' ? t('Switch to Arabic') : t('Switch to English')}
        style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: SP.xs,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: C.inkMuted,
            padding: SP.xs,
            ...TYPE.caption,
            fontWeight: 500,
            ...(block ? { paddingInline: 0 } : {}),
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = C.ink; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = C.inkMuted; }}
    >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
            <circle cx="12" cy="12" r="9" />
            <path d="M3 12h18" />
            <path d="M12 3a15 15 0 0 1 0 18a15 15 0 0 1 0-18z" />
        </svg>
        <span style={{ fontFamily: language === 'ar' ? FONT.arabic : FONT.body }}>
            {language === 'en' ? 'EN' : 'ع'}
        </span>
    </button>
);

const LandingLayout = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { language, isRTL, toggleLanguage, t } = useLanguage();

    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [isScrolled, setIsScrolled] = useState(false);

    // A short grace period on mouse-out. The bridge keeps the pointer inside
    // the subtree on a straight path, but a diagonal move toward a menu item
    // can still clip the corner of both boxes; without the delay that reads as
    // the menu vanishing as you reach for it.
    const closeTimer = useRef(null);

    const cancelClose = useCallback(() => {
        if (closeTimer.current) {
            clearTimeout(closeTimer.current);
            closeTimer.current = null;
        }
    }, []);

    const closeDropdowns = useCallback(() => {
        cancelClose();
        setActiveDropdown(null);
    }, [cancelClose]);

    const openDropdown = useCallback((id) => {
        cancelClose();
        setActiveDropdown(id);
    }, [cancelClose]);

    const scheduleClose = useCallback(() => {
        cancelClose();
        closeTimer.current = setTimeout(() => setActiveDropdown(null), 160);
    }, [cancelClose]);

    useEffect(() => cancelClose, [cancelClose]);

    useEffect(() => {
        window.scrollTo(0, 0);
        setIsMobileMenuOpen(false);
        setActiveDropdown(null);
    }, [location.pathname]);

    useEffect(() => {
        const onScroll = () => setIsScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    // Escape closes whatever is open. Without this the mobile menu can only be
    // dismissed by tapping the toggle, which is a trap on a small screen.
    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key !== 'Escape') return;
            setIsMobileMenuOpen(false);
            setActiveDropdown(null);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    const isActivePage = (path) => location.pathname === path;

    const goToDemo = () => {
        if (location.pathname === '/contact-us') {
            document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
        } else {
            navigate('/contact-us');
        }
    };

    // The `downloads` entry is gone with the desktop section it pointed at.
    const navigationSections = [
        { id: 'home', title: t('Home'), path: '/', isStandalone: true },
        {
            id: 'product',
            title: t('Product'),
            items: [
                { title: t('Our Services'), path: '/our-services' },
                { title: t('How it Works'), path: '/how-it-works' },
            ],
        },
        {
            id: 'support',
            title: t('Support'),
            items: [
                { title: t('FAQ'), path: '/faq' },
                { title: t('Contact Us'), path: '/contact-us' },
            ],
        },
        {
            id: 'company',
            title: t('Company'),
            items: [{ title: t('About Us'), path: '/about-us' }],
        },
    ];

    // ---- shared style fragments ------------------------------------------

    return (
        <div style={{ background: C.paper, color: C.ink, fontFamily: isRTL ? FONT.arabic : FONT.body, minHeight: '100vh' }} dir={isRTL ? 'rtl' : 'ltr'}>
            <header
                style={{
                    position: 'sticky',
                    insetBlockStart: 0,
                    zIndex: 50,
                    background: C.paper,
                    borderBlockEnd: `1px solid ${C.line}`,
                    boxShadow: isScrolled ? `0 1px 0 ${C.line}` : 'none',
                    transition: 'box-shadow .18s ease',
                }}
            >
                <div style={{ ...S.container, paddingInline: SP.xl }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 64, gap: SP.lg }}>
                        <Link
                            to="/"
                            onClick={closeDropdowns}
                            aria-label={t('Aqana home')}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: SP.sm, textDecoration: 'none', color: C.ink }}
                        >
                            <ArchMark size={22} />
                            <span style={{ fontFamily: FONT.display, fontSize: '1.0625rem', fontWeight: 600, letterSpacing: '-0.02em' }}>
                                {t('Aqana')}
                            </span>
                        </Link>

                        <nav className="hidden md:flex" style={{ alignItems: 'center', gap: SP['2xl'] }}>
                            {navigationSections.map((section) =>
                                section.isStandalone ? (
                                    <Link
                                        key={section.id}
                                        to={section.path}
                                        onClick={closeDropdowns}
                                        style={navLink(isActivePage(section.path))}
                                    >
                                        {section.title}
                                    </Link>
                                ) : (
                                    <DropdownMenu
                                        key={section.id}
                                        section={section}
                                        isOpen={activeDropdown === section.id}
                                        isActivePage={isActivePage}
                                        onOpen={() => openDropdown(section.id)}
                                        onLeave={scheduleClose}
                                        onClose={closeDropdowns}
                                        onToggle={() => (activeDropdown === section.id ? closeDropdowns() : openDropdown(section.id))}
                                    />
                                )
                            )}
                        </nav>

                        <div className="hidden md:flex" style={{ alignItems: 'center', gap: SP.lg }}>
                            <LanguageToggle language={language} toggleLanguage={toggleLanguage} t={t} />
                            <a
                                href="/login"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ ...S.btnSecondary, whiteSpace: 'nowrap' }}
                                onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.ink; }}
                                onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.lineStrong; }}
                            >
                                {t('Log in')}
                            </a>
                            <button type="button" onClick={goToDemo} style={{ ...S.btnPrimary, whiteSpace: 'nowrap' }}>
                                {t('Get a demo')}
                            </button>
                        </div>

                        <button
                            type="button"
                            className="md:hidden"
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            aria-expanded={isMobileMenuOpen}
                            aria-label={isMobileMenuOpen ? t('Close menu') : t('Open menu')}
                            // No inline `display` here: it would beat Tailwind's md:hidden and
                            // leave the hamburger showing beside the desktop nav.
                            style={{ alignItems: 'center', justifyContent: 'center', width: 40, height: 40, background: 'none', border: 'none', color: C.ink, cursor: 'pointer', lineHeight: 0 }}
                        >
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                                {isMobileMenuOpen ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                ) : (
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16M4 12h16M4 17h16" />
                                )}
                            </svg>
                        </button>
                    </div>
                </div>

                {isMobileMenuOpen && (
                    <div
                        className="md:hidden"
                        style={{ background: C.paper, borderBlockStart: `1px solid ${C.line}`, paddingBlock: SP.lg, paddingInline: SP.xl }}
                    >
                        {navigationSections.map((section) => (
                            <div key={section.id} style={{ marginBlockEnd: SP.lg }}>
                                {section.isStandalone ? (
                                    <Link
                                        to={section.path}
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        style={{ ...TYPE.title, color: isActivePage(section.path) ? C.ink : C.inkMuted, textDecoration: 'none', display: 'block', paddingBlock: SP.sm }}
                                    >
                                        {section.title}
                                    </Link>
                                ) : (
                                    <>
                                        <span style={{ ...TYPE.label, color: C.inkFaint, display: 'block', marginBlockEnd: SP.sm }}>
                                            {section.title}
                                        </span>
                                        {section.items.map((item) => (
                                            <Link
                                                key={item.path}
                                                to={item.path}
                                                onClick={() => setIsMobileMenuOpen(false)}
                                                style={{ ...TYPE.title, color: isActivePage(item.path) ? C.ink : C.inkMuted, textDecoration: 'none', display: 'block', paddingBlock: SP.sm, paddingInlineStart: SP.md }}
                                            >
                                                {item.title}
                                            </Link>
                                        ))}
                                    </>
                                )}
                            </div>
                        ))}

                        <div style={{ borderBlockStart: `1px solid ${C.line}`, paddingBlockStart: SP.lg, display: 'flex', flexDirection: 'column', gap: SP.md }}>
                            <LanguageToggle language={language} toggleLanguage={toggleLanguage} t={t} />
                            <a
                                href="/login"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => setIsMobileMenuOpen(false)}
                                style={{ ...S.btnSecondary, width: '100%' }}
                            >
                                {t('Log in')}
                            </a>
                            <button
                                type="button"
                                onClick={() => { goToDemo(); setIsMobileMenuOpen(false); }}
                                style={{ ...S.btnPrimary, width: '100%' }}
                            >
                                {t('Get a demo')}
                            </button>
                        </div>
                    </div>
                )}
            </header>

            {activeDropdown && (
                <div
                    role="presentation"
                    onClick={closeDropdowns}
                    style={{ position: 'fixed', inset: 0, zIndex: 40 }}
                />
            )}

            <main>{children}</main>

            <footer style={{ background: C.ink, color: C.paper, paddingBlock: SP['4xl'], paddingInline: SP.xl }}>
                <div style={S.container}>
                    <div
                        style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${Math.round(W.content / 5)}px), 1fr))`,
                            gap: SP['2xl'],
                            alignItems: 'start',
                        }}
                    >
                        <div style={{ gridColumn: 'span 1', minWidth: 240 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: SP.sm, marginBlockEnd: SP.md }}>
                                <ArchMark size={26} color={C.paper} opacity={0.9} />
                                <span style={{ fontFamily: FONT.display, fontSize: '1.125rem', fontWeight: 600, letterSpacing: '-0.02em' }}>
                                    {t('Aqana')}
                                </span>
                            </div>
                            <p style={{ ...TYPE.body, color: A.paperFaint, marginBlockEnd: SP.xl, maxWidth: 260 }}>
                                {t('The till and the back office in one system.')}
                            </p>
                            <StoreBadges app="pos" align="start" />
                        </div>

                        <div>
                            <h4 style={footerHeading}>{t('Product')}</h4>
                            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: SP.md }}>
                                <li>
                                    <Link to="/our-services" style={footerLink}
                                        onMouseEnter={(e) => { e.currentTarget.style.color = C.paper; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.color = A.paperFaint; }}>
                                        {t('Our Services')}
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/how-it-works" style={footerLink}
                                        onMouseEnter={(e) => { e.currentTarget.style.color = C.paper; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.color = A.paperFaint; }}>
                                        {t('How it Works')}
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/faq" style={footerLink}
                                        onMouseEnter={(e) => { e.currentTarget.style.color = C.paper; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.color = A.paperFaint; }}>
                                        {t('FAQ')}
                                    </Link>
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h4 style={footerHeading}>{t('Company')}</h4>
                            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: SP.md }}>
                                <li>
                                    <Link to="/about-us" style={footerLink}
                                        onMouseEnter={(e) => { e.currentTarget.style.color = C.paper; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.color = A.paperFaint; }}>
                                        {t('About Us')}
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/contact-us" style={footerLink}
                                        onMouseEnter={(e) => { e.currentTarget.style.color = C.paper; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.color = A.paperFaint; }}>
                                        {t('Contact Us')}
                                    </Link>
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h4 style={footerHeading}>{t('Legal')}</h4>
                            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: SP.md }}>
                                <li>
                                    <Link to="/privacy-policy" style={footerLink}
                                        onMouseEnter={(e) => { e.currentTarget.style.color = C.paper; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.color = A.paperFaint; }}>
                                        {t('Privacy Policy')}
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/terms-of-service" style={footerLink}
                                        onMouseEnter={(e) => { e.currentTarget.style.color = C.paper; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.color = A.paperFaint; }}>
                                        {t('Terms of Service')}
                                    </Link>
                                </li>
                                <li>
                                    <Link to="/data-deletion" style={footerLink}
                                        onMouseEnter={(e) => { e.currentTarget.style.color = C.paper; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.color = A.paperFaint; }}>
                                        {t('Data Deletion')}
                                    </Link>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <div
                        style={{
                            borderBlockStart: `1px solid ${A.paperLine}`,
                            marginBlockStart: SP['3xl'],
                            paddingBlockStart: SP.xl,
                            ...TYPE.caption,
                            color: A.paperFaint,
                        }}
                    >
                        {t('© 2026 Aqana. All rights reserved.')}
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default LandingLayout;
